import { AppError } from '@core/errors/app-error';
import { AdminPermission, AdminProfile } from './admin-profile';
import { AdminProfileDto, TokenPairDto } from './graphql/admin-auth.dto';
import { TokenPair } from './token-pair';

const KNOWN_PERMISSIONS: readonly AdminPermission[] = [
  'MEMBER_SEARCH',
  'MEMBER_VIEW_MASKED',
  'MEMBER_VIEW_FULL',
  'EARNING_CONFIG_VIEW',
  'EARNING_CONFIG_CHANGE',
  'EARNING_CONFIG_HISTORY',
  'RECEIPT_QUEUE_VIEW',
  'REFERENCE_DATA_VIEW',
  'REFERENCE_DATA_IMPORT',
];

/** Validates a `TokenPairResponse`; a malformed pair never starts a session. */
export function toTokenPair(dto: TokenPairDto | null | undefined): TokenPair {
  if (
    !dto ||
    typeof dto.accessToken !== 'string' ||
    !dto.accessToken ||
    typeof dto.refreshToken !== 'string' ||
    !dto.refreshToken ||
    typeof dto.accessTokenExpiresIn !== 'number' ||
    typeof dto.refreshTokenExpiresIn !== 'number'
  ) {
    throw new AppError('server', 'The sign-in response was incomplete.');
  }
  return {
    accessToken: dto.accessToken,
    accessTokenExpiresIn: dto.accessTokenExpiresIn,
    refreshToken: dto.refreshToken,
    refreshTokenExpiresIn: dto.refreshTokenExpiresIn,
  };
}

/** Unknown permission values are dropped (UX only; the API keeps enforcing its own matrix). */
export function toAdminProfile(dto: AdminProfileDto | null | undefined): AdminProfile {
  if (!dto || typeof dto.adminId !== 'string' || typeof dto.email !== 'string') {
    throw new AppError('server', 'The admin profile response was incomplete.');
  }
  const permissions = (dto.permissions ?? []).filter((p): p is AdminPermission =>
    KNOWN_PERMISSIONS.includes(p as AdminPermission),
  );
  return {
    adminId: dto.adminId,
    email: dto.email,
    displayName: dto.displayName?.trim() ? dto.displayName.trim() : null,
    roles: [...(dto.roles ?? [])],
    permissions,
  };
}
